import React from "react";
import {mergeAttributes, Node} from "@tiptap/core";
import {NodeViewWrapper, ReactNodeViewRenderer} from "@tiptap/react";
import type {NodeViewProps} from "@tiptap/react";
import {Box, IconButton, Stack, Tooltip} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {NodeSelection} from "@tiptap/pm/state";
import type {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import {
    findPrintoutVariable,
    getPrintoutVariableChipLabel,
    getPrintoutVariableDisplayReference,
    normalizePrintoutDateFormat,
    parsePrintoutVariableReference,
} from "./printoutVariables";
import type {PrintoutDateFormat} from "./printoutVariables";
import {getPrintoutVariableChipStyle} from "./chipStyles";
import {
    usePrintoutFloatingMenuPlacement,
    usePrintoutNodeSelection,
} from "../blocks/shared/PrintoutBlockShell";

export interface PrintoutVariableNodeAttrs {
    dateFormat?: PrintoutDateFormat | null;
    fallback?: string | null;
    key: string | null;
    language?: string | null;
    previewUnresolved?: boolean | null;
    previewValue?: string | null;
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        printoutVariable: {
            insertPrintoutVariable: (attrs: PrintoutVariableNodeAttrs) => ReturnType;
        };
    }
}

const getPrintoutVariableReference = (attrs: PrintoutVariableNodeAttrs) => {
    if (!attrs.key) return "";
    return attrs.language ? `${attrs.key}:${attrs.language}` : attrs.key;
};

// Catalogue labels disable i18next namespace parsing because they may contain colons.
const getVariableMenuLabel = (
    variable: ReturnType<typeof findPrintoutVariable>,
    displayReference: string,
    variableText: string,
    t: TFunction,
) => {
    if (variable) return `${t(variable.scopeLabel, {nsSeparator: false})} ${variableText}`;

    return displayReference || variableText;
};

const PrintoutVariableNodeView = ({deleteNode, editor, getPos, node}: NodeViewProps) => {
    const {t} = useTranslation();
    const menuRef = usePrintoutFloatingMenuPlacement<HTMLSpanElement>();
    const attrs = node.attrs as PrintoutVariableNodeAttrs;
    const reference = getPrintoutVariableReference(attrs);
    const displayReference = getPrintoutVariableDisplayReference(reference);
    const chipText = attrs.previewValue || getPrintoutVariableChipLabel(displayReference);
    const variable = attrs.key ? findPrintoutVariable(attrs.key) : undefined;
    const editable = editor.isEditable;
    // Shares the block shell's selection contract: an enclosing node selection keeps Tiptap's
    // `selected` prop true, so chip chrome cannot be derived from that prop.
    const variableIsActive = usePrintoutNodeSelection(editor, getPos, node.type.name).nodeSelected;
    const menuLabel = getVariableMenuLabel(variable, displayReference, t("variable"), t);
    const title = variable
        ? `${attrs.previewUnresolved ? "Unresolved: " : ""}${variable.label}${attrs.language ? ` (${attrs.language.toUpperCase()})` : ""}`
        : displayReference;
    const wrapperClassName = [
        "printout-variable-node",
        variableIsActive ? "is-selected" : "",
    ].filter(Boolean).join(" ");
    const chipClassName = [
        "printout-variable-chip",
        variableIsActive ? "is-selected" : "",
        attrs.previewUnresolved ? "is-unresolved" : "",
    ].filter(Boolean).join(" ");

    const selectVariableAtCurrentPosition = () => {
        const position = getPos();
        if (typeof position !== "number") return false;

        editor.commands.setNodeSelection(position);
        editor.view.focus();
        return true;
    };

    const selectVariable = (event: React.MouseEvent<HTMLElement>) => {
        if (!editable) return;
        const target = event.target as HTMLElement;

        if (target.closest("[data-printout-variable-action]")) return;

        event.preventDefault();
        selectVariableAtCurrentPosition();
    };

    const keepEditorSelection = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
    };

    const duplicateVariable = () => {
        const position = getPos();
        if (typeof position !== "number") return;

        const insertPosition = position + node.nodeSize;
        const duplicateNode = editor.schema.nodeFromJSON(node.toJSON());
        const transaction = editor.state.tr.insert(insertPosition, duplicateNode);

        editor.view.dispatch(
            transaction
                .setSelection(NodeSelection.create(transaction.doc, insertPosition))
                .scrollIntoView(),
        );
        editor.view.focus();
    };

    const selectChipText = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const selection = window.getSelection();
        if (!selection) return;

        const range = document.createRange();
        range.selectNodeContents(event.currentTarget);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    return (
        <NodeViewWrapper
            as="span"
            contentEditable={false}
            className={wrapperClassName}
            data-printout-variable-reference={displayReference}
            data-printout-variable-unresolved={attrs.previewUnresolved ? "true" : undefined}
            // Prevent an enclosing block shell from stealing this node selection.
            data-printout-selectable-node=""
            onMouseDown={selectVariable}
            title={editable ? title : undefined}
        >
            {editable && variableIsActive && (
                <Stack
                    alignItems="center"
                    className="printout-variable-menu"
                    component="span"
                    data-printout-variable-action
                    direction="row"
                    gap={0.25}
                    ref={menuRef}
                >
                    <Box
                        className="printout-variable-name-label"
                        component="span"
                        onMouseDown={keepEditorSelection}
                    >
                        {menuLabel}
                    </Box>
                    <Tooltip title={t("Duplicate variable")}>
                        <IconButton
                            aria-label={t("Duplicate variable")}
                            className="printout-variable-menu-icon"
                            onClick={duplicateVariable}
                            onMouseDown={keepEditorSelection}
                            size="small"
                        >
                            <ContentCopyIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Delete variable")}>
                        <IconButton
                            aria-label={t("Delete variable")}
                            className="printout-variable-menu-icon"
                            onClick={deleteNode}
                            onMouseDown={keepEditorSelection}
                            size="small"
                        >
                            <DeleteOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}
            <Box
                className={chipClassName}
                component="span"
                onDoubleClick={selectChipText}
                style={getPrintoutVariableChipStyle(node.marks, {editable})}
            >
                {chipText}
            </Box>
        </NodeViewWrapper>
    );
};

export const PrintoutVariableNode = Node.create({
    name: "printoutVariable",

    group: "inline",
    inline: true,
    atom: true,
    marks: "_",
    selectable: true,
    // Inline variables move by cut/paste; drag-and-drop is reserved for blocks.
    draggable: false,

    addAttributes() {
        return {
            key: {
                default: null,
                parseHTML: element => element.getAttribute("data-printout-variable-key"),
                renderHTML: attributes => ({
                    "data-printout-variable-key": attributes.key,
                }),
            },
            language: {
                default: null,
                parseHTML: element => element.getAttribute("data-printout-variable-language"),
                renderHTML: attributes => attributes.language
                    ? {"data-printout-variable-language": attributes.language}
                    : {},
            },
            fallback: {
                default: null,
                parseHTML: element => element.getAttribute("data-printout-variable-fallback"),
                renderHTML: attributes => typeof attributes.fallback === "string"
                    ? {"data-printout-variable-fallback": attributes.fallback}
                    : {},
            },
            dateFormat: {
                default: null,
                parseHTML: element => normalizePrintoutDateFormat(
                    element.getAttribute("data-printout-variable-date-format"),
                ) ?? null,
                renderHTML: attributes => {
                    const dateFormat = normalizePrintoutDateFormat(attributes.dateFormat);
                    return dateFormat ? {"data-printout-variable-date-format": dateFormat} : {};
                },
            },
            previewValue: {
                default: null,
                rendered: false,
            },
            previewUnresolved: {
                default: false,
                rendered: false,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="printout-variable"]',
            },
        ];
    },

    renderHTML({HTMLAttributes, node}) {
        const attrs = node.attrs as PrintoutVariableNodeAttrs;
        const reference = getPrintoutVariableReference(attrs);
        const displayReference = getPrintoutVariableDisplayReference(reference);

        return [
            "span",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-variable",
                "data-printout-variable-reference": displayReference,
                class: "printout-variable-chip",
            }),
            getPrintoutVariableChipLabel(displayReference),
        ];
    },

    renderText({node}) {
        const attrs = node.attrs as PrintoutVariableNodeAttrs;
        const reference = getPrintoutVariableReference(attrs);
        return getPrintoutVariableChipLabel(getPrintoutVariableDisplayReference(reference));
    },

    addCommands() {
        return {
            insertPrintoutVariable: attrs => ({commands}) => {
                const parsed = attrs.key ? parsePrintoutVariableReference(
                    attrs.language ? `${attrs.key}:${attrs.language}` : attrs.key,
                ) : {key: null, language: undefined};

                return commands.insertContent({
                    type: this.name,
                    attrs: {
                        dateFormat: normalizePrintoutDateFormat(attrs.dateFormat) ?? null,
                        fallback: typeof attrs.fallback === "string" ? attrs.fallback : null,
                        key: parsed.key,
                        language: parsed.language ?? null,
                    },
                });
            },
        };
    },

    addKeyboardShortcuts() {
        const deleteVariable = (direction: "after" | "before") => {
            const {state, view} = this.editor;
            const {selection} = state;

            if (selection instanceof NodeSelection && selection.node.type === this.type) {
                view.dispatch(state.tr.deleteSelection().scrollIntoView());
                return true;
            }

            if (!selection.empty) return false;

            const adjacentNode = direction === "before"
                ? selection.$from.nodeBefore
                : selection.$from.nodeAfter;

            if (!adjacentNode || adjacentNode.type !== this.type) return false;

            const from = direction === "before"
                ? selection.$from.pos - adjacentNode.nodeSize
                : selection.$from.pos;
            const to = direction === "before"
                ? selection.$from.pos
                : selection.$from.pos + adjacentNode.nodeSize;

            view.dispatch(state.tr.delete(from, to).scrollIntoView());
            return true;
        };

        return {
            Backspace: () => deleteVariable("before"),
            Delete: () => deleteVariable("after"),
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutVariableNodeView);
    },
});
